import React from 'react'
import { Document, Page, View, Text } from '@react-pdf/renderer'
import { PDFDocumentProps } from '../types'
import { styles } from '../styles'
import { CompactPaceTable } from '../components/PDFTable'
import { formatTime } from '../../calculations'
import { formatNutritionItems, calculateTotalDistance } from '../utils'
import { calculateSegmentTimeline } from '../../timeline'

export function CompactCardDocument({ run }: PDFDocumentProps) {
  // Ensure segments and aid stations are properly ordered
  const segments = (run.segments || []).sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
  const aidStations = (run.aid_stations || []).sort((a, b) => a.at_distance_km - b.at_distance_km)
  const planItems = run.plan_items || []

  // Build the complete timeline with segments and aid stations
  const timeline = calculateSegmentTimeline(segments, aidStations, undefined)

  // Process timeline events to add nutrition
  const processedTimeline = [
    // Add segments
    ...timeline.segments.map(segmentData => {
      const segment = segmentData.segment
      const segmentItems = planItems.filter(planItem => planItem.segment_id === segment.id)
      const nutritionItems = formatNutritionItems(segmentItems, true)

      return {
        type: 'segment' as const,
        data: {
          ...segment,
          segmentStart: segmentData.cumulativeDistance - segment.distance_km,
          segmentEnd: segmentData.cumulativeDistance,
          pace: segmentData.pace,
          segmentTravelTime: segmentData.travelTime,
          segmentStopTime: segmentData.stopTime
        },
        distance: segmentData.cumulativeDistance,
        cumulativeTime: segmentData.departureTime,
        nutritionDisplay: nutritionItems.length > 0 ? nutritionItems.join(', ') : '-'
      }
    }),
    // Add aid stations
    ...timeline.aidStations.map(stationData => {
      const station = stationData.station
      const stationItems = planItems.filter(planItem => planItem.aid_station_id === station.id)

      // Simple nutrition display
      const nutritionItems = stationItems.map(item => item.name || 'Unknown').filter(name => name !== 'Unknown')
      const nutritionDisplay = nutritionItems.length > 0 ? nutritionItems.join(', ') : '-'

      return {
        type: 'aid_station' as const,
        data: station,
        distance: station.at_distance_km,
        cumulativeTime: stationData.arrivalTime,
        nutritionDisplay
      }
    })
  ].sort((a, b) => a.distance - b.distance)

  return (
    <Document title={`${run.title} - Pace Card`}>
      <Page size="A4" style={styles.pageCompact}>
        {/* Simple header */}
        <View style={styles.compactHeader}>
          <View style={styles.compactHeaderCell}>
            <Text style={styles.compactHeaderText}>{run.title}</Text>
          </View>
          <View style={styles.compactHeaderCell}>
            <Text style={styles.compactHeaderText}>{run.date || '_______'}</Text>
          </View>
          <View style={styles.compactHeaderCell}>
            <Text style={styles.compactHeaderText}>{calculateTotalDistance(segments).toFixed(1)}km</Text>
          </View>
        </View>
        
        <View style={{ flex: 1 }}>
          <CompactPaceTable 
            timelineEvents={processedTimeline}
            formatTime={formatTime}
          />
        </View>

        {/* Notes section */}
        {run.notes && (
          <View style={styles.compactNotes}>
            <Text style={styles.compactNotesText}>{run.notes}</Text>
          </View>
        )}
        
        {/* Print instructions - very small at bottom */}
        <View style={{ position: 'absolute', bottom: 5, right: 5 }}>
          <Text style={{ fontSize: 6, color: '#999', textAlign: 'right' }}>
            Print at 100% scale • No fit to page
          </Text>
        </View>
      </Page>
    </Document>
  )
}